import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { get } from '../../utils/api';

function fmtTs(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
}

/**
 * Read-only migration log viewer for a single run id (e.g. scheduler-job-19).
 */
export default function MigrationRunLogDialog({ open, onClose, initialRunId = '', initialSearchQ = '' }) {
  const [runId, setRunId] = React.useState(initialRunId);
  const [q, setQ] = React.useState(initialSearchQ);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [total, setTotal] = React.useState(0);
  const [rows, setRows] = React.useState([]);

  React.useEffect(() => {
    if (!open) return;
    setRunId(initialRunId);
    setQ(initialSearchQ);
  }, [open, initialRunId, initialSearchQ]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await get('/api/manage/civi-wc-product-mapping/migration-logs', {
        q: q || undefined,
        runId: runId || undefined,
        level: 'all',
        limit: 500,
      });
      setTotal(Number(res?.totalRecords || 0));
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(e?.data?.error || e?.message || 'Failed to load logs');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, runId]);

  React.useEffect(() => {
    if (!open) return undefined;
    load();
    return undefined;
  }, [open, load]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 2000 }}
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle>Migration log — run</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', pt: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="Run ID"
            value={runId}
            onChange={(e) => setRunId(e.target.value)}
            sx={{ minWidth: 260, flex: 1 }}
            placeholder="scheduler-job-…"
          />
          <TextField
            size="small"
            label="Search logs"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 220, flex: 1 }}
            placeholder="Filter lines…"
          />
          <Button variant="outlined" onClick={load} disabled={loading} sx={{ alignSelf: { sm: 'center' } }}>
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          Matched rows: {total}
        </Typography>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, flex: 1, overflow: 'auto', maxHeight: '56vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 200 }}>Timestamp</TableCell>
                <TableCell sx={{ width: 80 }}>Level</TableCell>
                <TableCell sx={{ width: 220 }}>Run ID</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>No log lines for this run / search.</TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={`dlg-${r.id}-${r.ts}`} hover>
                  <TableCell>{fmtTs(r.ts)}</TableCell>
                  <TableCell>{String(r.level || '')}</TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.75rem' }}>{String(r.runId || '—')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}>
                    {String(r.message || '')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
